import {
  createAsyncThunk,
} from '@reduxjs/toolkit';

import type { EntityDataPubType } from '@/domain/entityBase';
import type { PublicApiSettingsJson } from '@/domain/payment/service/entity/publicApiSettingsEntity';
import type { ThunkApiType } from '@/store';
import type { PhotoOrderJsonPub, PhotoOrderJsonPvt } from '@/types/domain/photo/entities/photoOrderEntity';

import { paymentSlice } from '@/domain/payment/redux';
import {
  photoSlice,
} from '@/domain/photo/redux';
import { uiSlice } from '@/domain/ui/redux';
import { service } from '@/share';
import { dateFormat } from '@/utils/date';

export const refreshSalesPhotoList = createAsyncThunk<
  void,
  {
    /** 親子ID */
    parentChildId: string,
  },
  ThunkApiType
>(
  'photo/refreshSalesPhotoList',
  async (input, thunkApi) => {
    const result = await service.photo.getPhotoSalesList(null);
    thunkApi.dispatch(
      photoSlice.actions.setSalesPhotos(result.items.filter((v) => `${v.data.parentId}_${v.data.childId}` === input.parentChildId)),
    );
  },
);

export const refreshApiSettings = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
  },
  ThunkApiType
>(
  'photo/refreshApiSettings',
  async (input, thunkApi) => {
    const settings = await service.payment.getApiSettings(input.nurseryId);
    if (settings) {
      const map: { [key: string]: PublicApiSettingsJson } = {};
      map[input.nurseryId] = settings;
      thunkApi.dispatch(paymentSlice.actions.setApiSettings(map));
    }
  },
);

/**
 * 写真を購入する
 */
export const execPurchasePhotos = createAsyncThunk<
  void,
  {
    /** 価格 */
    amount: number,
    /** 施設ID */
    nurseryId: string,
    /** 写真リスト */
    photos: {
      /** 写真ID */
      photoId: string,
      /** 写真アイテムID */
      photoItemId: string,
      /** アイテム数 */
      numberOfItems: number,
    }[],
    /** グループID */
    groupId: string,
  },
  ThunkApiType
>(
  'photo/execPurchasePhotos',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      const { childId } = thunkApi.getState().child;
      const result = await service.photo.photoPayByNursery(
        input.nurseryId,
        input.groupId,
        childId,
        input.amount,
        input.photos,
      );
      if (result.message !== 'OK') {
        thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'error', content: result.message }));
      } else {
        thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: '支払い処理が完了しました' }));
      }
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);

/**
 * クレジットカードで写真を購入する
 */
export const execPurchasePhotosByCreditCard = createAsyncThunk<
  void,
  {
    /** 価格 */
    amount: number,
    /** カードID */
    cardId: string,
    /** 施設ID */
    nurseryId: string,
    /** 写真リスト */
    photos: {
      /** 写真ID */
      photoId: string,
      /** 写真アイテムID */
      photoItemId: string,
      /** アイテム数 */
      numberOfItems: number,
    }[],
    /** グループID */
    groupId: string,
  },
  ThunkApiType
>(
  'photo/execPurchasePhotosByCreditCard',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      const { childId } = thunkApi.getState().child;
      const result = await service.photo.photoPayByCard(
        input.nurseryId,
        input.groupId,
        childId,
        input.amount,
        input.photos,
        input.cardId,
      );
      if (result.message !== 'OK') {
        thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'error', content: result.message }));
      } else {
        thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: '支払い処理が完了しました' }));
      }
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);

export const refreshPhotoItemList = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
  },
  ThunkApiType
>(
  'photo/refreshPhotoItemList',
  async (input, thunkApi) => {
    const { items } = await service.photo.getPhotoItemList(input.nurseryId, null);
    thunkApi.dispatch(photoSlice.actions.setPhotoItemList(items));
  },
);

export const refreshPhotoOrderList = createAsyncThunk<
  void,
  {
    /** 親子ID */
    parentChildId: string,
  },
  ThunkApiType
>(
  'photo/refreshPhotoOrderList',
  async (input, thunkApi) => {
    const { nextToken, items } = await service.photo.getPhotoOrderList(null);
    thunkApi.dispatch(photoSlice.actions.setPhotoOrdersToken(nextToken));
    thunkApi.dispatch(
      photoSlice.actions.setPhotoOrders(items.filter((v) => `${v.data.parentId}_${v.data.childId}` === input.parentChildId)),
    );
  },
);

export const downloadOrderedPhotoFiles = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
    /** アイテム */
    item: EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub>
  },
  ThunkApiType
>(
  'photo/downloadOrderedPhotoFiles',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      // url取得
      const resultUrlList = await service.photo.getOrderedPhotoUrls(input.nurseryId, input.item);
      if (resultUrlList) {
        thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: 'ダウンロードを開始しました' }));
        await service.photo.downloadPhotoFiles(
          input.item.data.salesGroupName,
          resultUrlList,
          (message, type) => thunkApi.dispatch(uiSlice.actions.setMessage({
            key: type, content: message,
          })),
        );
      } else {
        thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'error', content: 'ダウンロード対象が取得できませんでした' }));
      }
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);

export const downloadOrderedPhotoFile = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
    /** アイテム */
    item: EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub>,
    /** 写真ID */
    photoId: string,
  },
  ThunkApiType
>(
  'photo/downloadOrderedPhotoFile',
  async (input, thunkApi) => {
    thunkApi.dispatch(uiSlice.actions.startBlocking());
    try {
      // url取得
      const resultUrlList = await service.photo.getOrderedPhotoUrls(input.nurseryId, input.item);
      if (resultUrlList) {
        const item = resultUrlList.find((v) => v.photoId === input.photoId);
        if (item) {
          const index = resultUrlList.indexOf(item);
          await service.photo.downloadPhotoFile(
            `${input.item.data.salesGroupName}_${index}_${dateFormat(new Date(), 'yyyyMMdd-HHmmss')}`,
            item,
            (message, type) => thunkApi.dispatch(uiSlice.actions.setMessage({
              key: type, content: message,
            })),
          );
          return;
        }
      }
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'error', content: 'ダウンロード対象が取得できませんでした' }));
    } finally {
      thunkApi.dispatch(uiSlice.actions.endBlocking());
    }
  },
);

export const cancelOrder = createAsyncThunk<
  void,
  {
    /** 施設ID */
    nurseryId: string,
    /** 注文 */
    order: EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub>,
  },
  ThunkApiType
>(
  'photo/cancelOrder',
  async (input, thunkApi) => {
    const result = await service.photo.cancelOrder(
      input.nurseryId,
      input.order,
    );
    if (result) {
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'warning', content: `注文キャンセルに失敗:${result.message}` }));
    } else {
      thunkApi.dispatch(uiSlice.actions.setMessage({ key: 'info', content: '写真注文をキャンセルしました' }));
    }
  },
);
